import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Highcharts from 'highcharts';
// Desde Highcharts 12 los módulos se registran solos al importarlos.
import 'highcharts/modules/stock';
import 'highcharts/modules/no-data-to-display';
import 'highcharts/highcharts-more';
import 'highcharts/modules/boost';
import 'highcharts/modules/solid-gauge';
import 'highcharts/indicators/indicators';
import 'highcharts/indicators/regressions';
import 'highcharts/modules/timeline';
import 'highcharts/modules/stock-tools';
// PNG y CSV del gráfico personalizado (P56). Apagado por defecto: cada gráfico lo prende si lo quiere.
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import { Options } from 'highcharts/highstock';
import { NavigationService } from '../../../modulos/navigation/navigation.service';
import { aplicarTemaChart, fuenteChart } from './tema-chart';

// Highcharts 12+ formatea fechas y números con Intl según el locale. Para que
// los gráficos se vean como con la v10: `hc-h23` mantiene las 24 horas (con
// `es-AR` a secas salen "11:15 a. m."), el punto decimal y el espacio de miles
// son los de antes, y los formatos del eje x son los de la v10 ("16. May" en
// el tick de las 00:00).
Highcharts.setOptions({
  exporting: { enabled: false },
  lang: {
    locale: 'es-AR-u-hc-h23',
    contextButtonTitle: 'Exportar',
    noData: 'Sin datos en el período elegido',
    downloadPNG: 'Descargar PNG',
    downloadCSV: 'Descargar CSV',
    decimalPoint: '.',
    thousandsSep: ' ',
    months: [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
    weekdays: [
      'Domingo',
      'Lunes',
      'Martes',
      'Miércoles',
      'Jueves',
      'Viernes',
      'Sábado',
    ],
    shortMonths: [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic',
    ],
    shortWeekdays: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  },
  xAxis: {
    // `boundary` de cada unidad lo usa el tick de la unidad inferior que cae
    // en su límite: sólo el cambio de día muestra la fecha, como en la v10.
    dateTimeLabelFormats: {
      millisecond: '%H:%M:%S.%L',
      second: '%H:%M:%S',
      minute: '%H:%M',
      hour: { main: '%H:%M', boundary: '%H:%M' },
      day: { main: '%e. %b', boundary: '%e. %b' },
      week: '%e. %b',
      month: { main: "%b '%y", boundary: '%e. %b' },
      year: { main: '%Y', boundary: "%b '%y" },
    },
  },
  // Aguja y pivote como en Highcharts 10: la v12 los rediseñó (punta ancha y
  // redondeada, pivote con borde).
  plotOptions: {
    gauge: {
      dial: { topWidth: 1, borderRadius: 0 },
      pivot: { borderWidth: 0 },
    },
  },
});

// La v12+ marca también el primer tick visible como cambio de día/año aunque
// no lo sea ("6. Oct" donde la v10 ponía "08:00"): si el tick no cae en el
// límite, vuelve al formato de su unidad.
Highcharts.addEvent(Highcharts.Tick, 'labelFormat' as any, (ctx: any) => {
  const unidad = ctx.tickPositionInfo?.unitName;
  if (!ctx.boundary || !unidad) return;
  const [, mes, dia, h, m, s, ms] = ctx.chart.time.toParts(ctx.value);
  const enPunto = !m && !s && !ms;
  const medianoche = enPunto && !h;
  const enLimite = {
    hour: enPunto,
    day: medianoche,
    month: medianoche && dia === 1,
    year: medianoche && dia === 1 && mes === 0,
  }[ctx.boundary as string];
  if (enLimite) return;
  ctx.dateTimeLabelFormat = ctx.chart.time.resolveDTLFormat(
    ctx.axis.options.dateTimeLabelFormats[unidad],
  ).main;
});

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() options!: Options;
  @Input() style?: string;
  @Input() constructorType: 'chart' | 'stockChart' = 'chart';
  @ViewChild('contenedor', { static: true })
  private contenedor!: ElementRef<HTMLDivElement>;
  chart?: Highcharts.Chart;
  private destroyRef = inject(DestroyRef);
  private navigation = inject(NavigationService);
  private zone = inject(NgZone);

  /**
   * Completa defaults sobre las opciones antes de crear o actualizar el
   * gráfico.
   */
  private setDefaults() {
    if (!this.options) return;
    if (!this.options.accessibility) {
      this.options.accessibility = {
        enabled: false,
      };
    }
    if (this.options?.chart && !this.options?.chart?.style) {
      this.options!.chart!.style = {
        fontFamily: fuenteChart(),
      };
    }
    // Hay configs en Mongo con límites como string ("1.2"): la v10 de
    // Highcharts los convertía sola, la v12+ no dibuja esa línea o banda.
    const ejes = [this.options.xAxis, this.options.yAxis].flat();
    for (const eje of ejes) {
      for (const linea of eje?.plotLines ?? []) {
        if (typeof linea.value === 'string') linea.value = Number(linea.value);
      }
      for (const banda of eje?.plotBands ?? []) {
        if (typeof banda.from === 'string') banda.from = Number(banda.from);
        if (typeof banda.to === 'string') banda.to = Number(banda.to);
      }
    }
  }

  // Highcharts corre fuera de la zona de Angular: sus listeners (mousemove del
  // tooltip, animaciones) no tienen que disparar detección de cambios en la app.
  private crear() {
    this.chart?.destroy();
    this.chart = undefined;
    if (!this.options) return;
    const fabrica =
      this.constructorType === 'stockChart'
        ? Highcharts.stockChart
        : Highcharts.chart;
    this.zone.runOutsideAngular(() => {
      this.chart = fabrica(this.contenedor.nativeElement, this.options);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.setDefaults();
    if (!changes['options'] || changes['options'].firstChange) return;
    if (!this.chart || !this.options) return this.crear();
    // oneToOne: series y ejes quedan iguales a los de las opciones nuevas.
    this.zone.runOutsideAngular(() =>
      this.chart!.update(this.options, true, true),
    );
  }

  ngAfterViewInit(): void {
    aplicarTemaChart();
    this.crear();
    // El tema va en los defaults globales, que Highcharts lee al crear el
    // gráfico: al cambiar de tema hay que recrearlo, un update no alcanza.
    this.navigation.modoOscuro$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        aplicarTemaChart();
        this.crear();
      });
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }
}
